<template>
	<div class="quote-hk02276-content-block">
		<div class="stock-title-row">
			<h1 class="stock-name">{{ displayName }}</h1>
			<span class="stock-code">{{ displayCode }}</span>
		</div>

		<div class="meta-row">
			<span>数据更新：{{ displayUpdateTime }}</span>
			<span>*进门提供技术支持</span>
		</div>

		<div class="price-row" :class="{ 'content-hidden': !dataInitDone }" :style="priceStyle">
			<div class="current-price">{{ displayPrice }}</div>
			<div class="currency">{{ displayCurrency }}</div>
			<div class="change-group">
				<svg
					v-if="changeDirection !== 'flat'"
					class="direction-icon"
					:class="{ 'direction-icon--up': changeDirection === 'up' }"
					width="12"
					height="14"
					viewBox="0 0 14 16"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					aria-hidden="true"
				>
					<path
						d="M7 1V15M7 15L1 9M7 15L13 9"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</svg>
				<span>{{ displayChange }}</span>
				<span>{{ displayChangePct }}</span>
			</div>
		</div>

		<div class="base-data-grid" :class="{ 'content-hidden': !dataInitDone }">
			<div class="data-item">
				<span class="label-text">最高</span>
				<span class="value-text">{{ displayHighPrice }}</span>
			</div>
			<div class="data-item data-item--right">
				<span class="label-text">成交额</span>
				<span class="value-text">{{ displayTurnoverValue }}</span>
			</div>
			<div class="data-item">
				<span class="label-text">最低</span>
				<span class="value-text">{{ displayLowPrice }}</span>
			</div>
			<div class="data-item data-item--right">
				<span class="label-text">成交量</span>
				<span class="value-text">{{ displayTurnoverVolume }}</span>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { computedStockColorByLang } from '@/assets/js/i18n';
import { useStore } from '@/utils/vue-api';
import { useStockVO } from '@rebuild/hooks/useStockVO';
import { usePolling } from '@rebuild/materials/shared/quote/usePolling';
import { useQuoteService } from '@rebuild/materials/shared/quote/useQuoteService';

const store = useStore();
const getIrName = computed(() => store.getters.getIrName);
const getIrMarketAndCode = computed(() => store.getters.getIrMarketAndCode || []);
const getCurrUnitTips = computed(() => store.getters.getCurrUnitTips);

const EMPTY_TEXT = '--';
const stockData = ref(null);
const { divideByHundred, stockColTransferUnit, computedVolume } = useStockVO();

const formatFallback = value => {
	if (value === null || value === undefined) return EMPTY_TEXT;
	if (typeof value === 'string' && value.trim() === '') return EMPTY_TEXT;
	if (value === '- -' || value === '-') return EMPTY_TEXT;
	return value;
};

const formatWithSign = value => {
	if (typeof value !== 'number') return EMPTY_TEXT;
	const formatted = formatFallback(stockColTransferUnit(value));
	return value > 0 ? `+${formatted}` : formatted;
};

const displayName = computed(() => formatFallback(getIrName.value));
const displayCode = computed(() => {
	const [market, code] = getIrMarketAndCode.value || [];
	if (!market || !code) return EMPTY_TEXT;
	return `${code}.${market.toLocaleUpperCase()}`;
});

const displayUpdateTime = computed(() => formatFallback(stockData.value?.tradingDay));
const displayCurrency = computed(() => formatFallback(getCurrUnitTips.value));
const displayPrice = computed(() => formatFallback(divideByHundred(stockData.value?.closePrice)));
const displayChange = computed(() => formatWithSign(stockData.value?.change));
const displayChangePct = computed(() => {
	if (typeof stockData.value?.changePct !== 'number') return EMPTY_TEXT;
	const base = formatFallback(stockColTransferUnit(stockData.value.changePct));
	const sign = stockData.value.changePct > 0 ? '+' : '';
	return `${sign}${base}%`;
});
const displayHighPrice = computed(() => formatFallback(stockColTransferUnit(stockData.value?.highPrice)));
const displayLowPrice = computed(() => formatFallback(stockColTransferUnit(stockData.value?.lowPrice)));
const displayTurnoverValue = computed(() => formatFallback(stockColTransferUnit(stockData.value?.turnoverValue)));
const displayTurnoverVolume = computed(() => {
	if (!stockData.value) return EMPTY_TEXT;
	return formatFallback(computedVolume(stockData.value));
});

const changeDirection = computed(() => {
	const change = stockData.value?.change;
	if (typeof change !== 'number' || change === 0) return 'flat';
	return change > 0 ? 'up' : 'down';
});
const priceStyle = computed(() => computedStockColorByLang(stockData.value?.change));

const { loadRealtimeQuoteData } = useQuoteService();
const realtimeTask = async () => {
	const res = await loadRealtimeQuoteData();
	stockData.value = res || null;

	if (!dataInitDone.value) {
		dataInitDone.value = true;
	}
};
usePolling(realtimeTask);

const dataInitDone = ref(false);
</script>

<style lang="less" scoped>
.quote-hk02276-content-block {
	background: #fff;
	padding: 0.14rem 0.12rem;

	.content-hidden {
		opacity: 0;
		pointer-events: none;
	}
}

.stock-title-row {
	display: flex;
	align-items: baseline;
	margin-bottom: 0.08rem;
}

.stock-name {
	margin: 0;
	margin-right: 0.1rem;
	font-size: 0.26rem;
	font-weight: 700;
	line-height: 0.34rem;
	letter-spacing: 0.01rem;
	color: #1d2129;
}

.stock-code {
	font-size: 0.16rem;
	font-weight: 400;
	line-height: 0.24rem;
	color: #4e5969;
}

.meta-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 0.24rem;
	font-size: 0.12rem;
	line-height: 0.18rem;
	color: #86909c;
}

.price-row {
	display: flex;
	align-items: baseline;
	margin-bottom: 0.28rem;
}

.current-price {
	margin-right: 0.06rem;
	font-size: 0.52rem;
	font-weight: 500;
	line-height: 1;
	letter-spacing: -0.01rem;
	font-family: 'DIN Alternate';
}

.currency {
	margin-right: 0.12rem;
	font-size: 0.16rem;
	font-weight: 500;
	line-height: 0.24rem;
}

.change-group {
	display: flex;
	align-items: center;
	column-gap: 0.06rem;
	font-size: 0.16rem;
	font-weight: 500;
	line-height: 0.24rem;
}

.direction-icon {
	margin-top: 0.01rem;
	flex-shrink: 0;
}

.direction-icon--up {
	transform: rotate(180deg);
}

.base-data-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	row-gap: 0.2rem;
	column-gap: 0.08rem;
	font-size: 0.14rem;
}

.data-item {
	display: flex;
	align-items: center;
	min-width: 0;
}

.data-item--right {
	padding-left: 0.04rem;
}

.label-text {
	width: 0.48rem;
	flex-shrink: 0;
	color: #86909c;
	text-align: justify;
	text-align-last: justify;
}

.value-text {
	margin-left: 0.12rem;
	font-weight: 500;
	color: #1d2129;
	white-space: nowrap;
}
</style>
